/**
 * Mail retention and legal holds (PRD Phase 6.5.1, ADR-0020). An
 * administrator sets how long mail is kept — all of it, Trash, Junk — and
 * places legal holds that keep what a matter needs, on whole mailboxes or on
 * the messages a query matches. The mail store applies retention once a day.
 *
 * The same panels serve the platform console and webmail's Organisation
 * area, through `AdminWhereContext`.
 */

import { useContext, useState } from 'react';
import type {
  LegalHoldList,
  LegalHoldQuery,
  LegalHoldRecord,
  MailboxList,
  NewLegalHold,
  RetentionPolicy,
  RetentionReport,
  RetentionStatus,
} from 'zitimail-shared-types';

import { api, describeError } from './api';
import { AdminWhereContext } from './mailboxes';
import { ErrorText } from './migrations';
import { ActionButton, Badge, Loaded, useLoad, when } from './ui';

// ─── Words ───────────────────────────────────────────────────────────────────

/** "1 year", "7 years", "90 days". */
export function daysInWords(days: number): string {
  if (days >= 365 && days % 365 === 0) {
    const years = days / 365;
    return `${years} ${years === 1 ? 'year' : 'years'}`;
  }
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

/** "2020-01-01" as "1 Jan 2020". */
export function dayInWords(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
}

/** `from boss@acme.test · subject contains "contract" · received before 1 Jan 2020`. */
export function queryInWords(q: LegalHoldQuery): string {
  const parts: string[] = [];
  if (q.from) {
    parts.push(`from ${q.from}`);
  }
  if (q.to) {
    parts.push(`to ${q.to}`);
  }
  if (q.subject) {
    parts.push(`subject contains "${q.subject}"`);
  }
  if (q.text) {
    parts.push(`text contains "${q.text}"`);
  }
  if (q.since) {
    parts.push(`received since ${dayInWords(q.since)}`);
  }
  if (q.before) {
    parts.push(`received before ${dayInWords(q.before)}`);
  }
  return parts.join(' · ');
}

/** The sentence `window.confirm` asks when a change would delete mail, or null when it would not. */
export function deletionWarning(before: RetentionPolicy, after: RetentionPolicy): string | null {
  const shorter = (key: keyof RetentionPolicy) => {
    const next = after[key];
    const prev = before[key];
    return next !== null && (prev === null || next < prev);
  };
  const what = [
    shorter('mail_days') && after.mail_days !== null
      ? `Mail older than ${daysInWords(after.mail_days)}`
      : null,
    shorter('trash_days') && after.trash_days !== null
      ? `Trash older than ${daysInWords(after.trash_days)}`
      : null,
    shorter('junk_days') && after.junk_days !== null
      ? `Junk older than ${daysInWords(after.junk_days)}`
      : null,
  ].filter((w): w is string => w !== null);
  if (what.length === 0) {
    return null;
  }
  const list = what.length === 1 ? what[0] : `${what.slice(0, -1).join(', ')} and ${what.at(-1)}`;
  return (
    `${list} will be deleted from every mailbox at the next daily pass, and cannot be ` +
    'recovered. Mail under a legal hold is kept. Continue?'
  );
}

// ─── Both panels ─────────────────────────────────────────────────────────────

export function RetentionPanel({ slug, active }: { slug: string; active: boolean }) {
  return (
    <>
      <RetentionPolicyPanel slug={slug} active={active} />
      {/* Holds stay open on a suspended tenant: a departing customer is
          exactly when one is placed. */}
      <LegalHoldsPanel slug={slug} active />
    </>
  );
}

// ─── Retention ───────────────────────────────────────────────────────────────

type Field = keyof RetentionPolicy;

interface FieldSpec {
  readonly key: Field;
  readonly label: string;
  readonly presets: readonly number[];
  readonly min: number;
  readonly max: number;
}

const FIELDS: readonly FieldSpec[] = [
  {
    key: 'mail_days',
    label: 'Keep all mail',
    presets: [365, 1095, 2555, 3650],
    min: 30,
    max: 36500,
  },
  { key: 'trash_days', label: 'Keep Trash', presets: [30, 90], min: 1, max: 3650 },
  { key: 'junk_days', label: 'Keep Junk', presets: [30, 90], min: 1, max: 3650 },
];

/** The select's value: `forever`, a preset's days, or `custom` with the days typed beside it. */
interface Choice {
  readonly pick: string;
  readonly days: string;
}

function choiceOf(spec: FieldSpec, days: number | null): Choice {
  if (days === null) {
    return { pick: 'forever', days: '' };
  }
  return spec.presets.includes(days)
    ? { pick: String(days), days: String(days) }
    : { pick: 'custom', days: String(days) };
}

/** The days a choice means; undefined when the typed number is not a whole number. */
function daysOf(choice: Choice): number | null | undefined {
  if (choice.pick === 'forever') {
    return null;
  }
  const text = choice.pick === 'custom' ? choice.days.trim() : choice.pick;
  return /^\d+$/.test(text) ? Number(text) : undefined;
}

function RetentionPolicyPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<RetentionStatus>(`${base(slug)}/retention`, { signal }),
    `retention:${slug}`,
  );
  return (
    <section className="adm-panel">
      <h3>Mail retention</h3>
      <p className="adm-muted">
        Once a day, mail older than this, by the date it arrived, is deleted from every mailbox,
        cold storage included, and every deletion is logged. Mail under a legal hold is never
        deleted.
      </p>
      <Loaded load={load}>
        {(status) => (
          <>
            <RetentionForm
              key={JSON.stringify(status.policy)}
              slug={slug}
              policy={status.policy}
              active={active}
              onSaved={reload}
            />
            <LastPass ranAt={status.ran_at} report={status.report} />
          </>
        )}
      </Loaded>
    </section>
  );
}

function RetentionForm({
  slug,
  policy,
  active,
  onSaved,
}: {
  slug: string;
  policy: RetentionPolicy;
  active: boolean;
  onSaved: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [form, setForm] = useState(
    () =>
      Object.fromEntries(
        FIELDS.map((spec) => [spec.key, choiceOf(spec, policy[spec.key])]),
      ) as Record<Field, Choice>,
  );
  const set = (key: Field, choice: Choice) => setForm({ ...form, [key]: choice });

  return (
    <>
      <div className="adm-form-grid">
        {FIELDS.map((spec) => {
          const choice = form[spec.key];
          return (
            <div key={spec.key} className="ret-field">
              <label>
                {spec.label}
                <select
                  value={choice.pick}
                  disabled={!active}
                  onChange={(e) => set(spec.key, { ...choice, pick: e.target.value })}
                >
                  <option value="forever">For ever</option>
                  {spec.presets.map((days) => (
                    <option key={days} value={String(days)}>
                      {daysInWords(days)}
                    </option>
                  ))}
                  <option value="custom">A number of days</option>
                </select>
              </label>
              {choice.pick === 'custom' && (
                <label className="ret-days">
                  <input
                    type="number"
                    min={spec.min}
                    max={spec.max}
                    value={choice.days}
                    disabled={!active}
                    aria-label={`${spec.label}, days`}
                    onChange={(e) => set(spec.key, { ...choice, days: e.target.value })}
                  />
                  days
                </label>
              )}
            </div>
          );
        })}
      </div>
      <p className="adm-muted adm-small">
        All mail: at least 30 days. Trash and Junk: 1 to 3650 days.
      </p>
      {active && (
        <ActionButton
          tone="ghost"
          run={async () => {
            const after = {
              mail_days: daysOf(form.mail_days),
              trash_days: daysOf(form.trash_days),
              junk_days: daysOf(form.junk_days),
            };
            for (const spec of FIELDS) {
              if (after[spec.key] === undefined) {
                throw new Error(`${spec.label}: enter a whole number of days.`);
              }
            }
            const next = after as RetentionPolicy;
            const warning = deletionWarning(policy, next);
            if (warning && !window.confirm(warning)) {
              return;
            }
            await api(`${base(slug)}/retention`, { method: 'POST', body: next });
            onSaved();
          }}
        >
          Save retention
        </ActionButton>
      )}
    </>
  );
}

function LastPass({ ranAt, report }: { ranAt: string | null; report: RetentionReport | null }) {
  if (!ranAt || !report) {
    return <p className="adm-muted adm-small">Retention has not run yet.</p>;
  }
  const removed: string[] = [];
  const counts: Array<[number | undefined, string]> = [
    [report.removed.mail, 'all mail'],
    [report.removed.trash, 'Trash'],
    [report.removed.junk, 'Junk'],
  ];
  let total = 0;
  for (const [n, label] of counts) {
    if (n !== undefined) {
      removed.push(`${n.toLocaleString()} from ${label}`);
      total += n;
    }
  }
  return (
    <div className="adm-small ret-last">
      <p className="adm-muted">
        Last pass {when(ranAt)}: {report.mailboxes.toLocaleString()}{' '}
        {report.mailboxes === 1 ? 'mailbox' : 'mailboxes'} checked, {total.toLocaleString()}{' '}
        {total === 1 ? 'message' : 'messages'} deleted
        {removed.length > 0 ? ` (${removed.join(', ')})` : ''}.
        {report.held_queries > 0 &&
          ` Messages matching ${report.held_queries} ${report.held_queries === 1 ? 'hold were' : 'holds were'} kept.`}
      </p>
      {report.error && (
        <p>
          <ErrorText text={report.error} />
        </p>
      )}
    </div>
  );
}

// ─── Legal holds ─────────────────────────────────────────────────────────────

function LegalHoldsPanel({ slug, active }: { slug: string; active: boolean }) {
  const { base } = useContext(AdminWhereContext);
  const [load, reload] = useLoad(
    (signal) => api<LegalHoldList>(`${base(slug)}/legal-holds`, { signal }),
    `legal-holds:${slug}`,
  );
  const [showReleased, setShowReleased] = useState(false);

  return (
    <section className="adm-panel">
      <h3>Legal holds</h3>
      <p className="adm-muted">
        Held mail is never deleted by retention, and a held mailbox cannot be purged. While any hold
        on messages is active, no mailbox in the organisation can be purged.
      </p>
      <Loaded load={load}>
        {({ holds }) => {
          const current = holds.filter((h) => h.released_at === null);
          const released = holds.filter((h) => h.released_at !== null);
          return (
            <>
              {current.length === 0 ? (
                <p className="adm-muted">No active holds.</p>
              ) : (
                <div className="ret-holds">
                  {current.map((h) => (
                    <HoldItem key={h.id} slug={slug} hold={h} active={active} onChanged={reload} />
                  ))}
                </div>
              )}
              {released.length > 0 && (
                <div className="adm-sub">
                  <div className="adm-panel-head">
                    <h4>Released in the last year</h4>
                    <button
                      type="button"
                      className="adm-copy"
                      aria-expanded={showReleased}
                      onClick={() => setShowReleased(!showReleased)}
                    >
                      {showReleased ? 'Hide' : `Show ${released.length}`}
                    </button>
                  </div>
                  {showReleased && (
                    <div className="ret-holds">
                      {released.map((h) => (
                        <HoldItem
                          key={h.id}
                          slug={slug}
                          hold={h}
                          active={false}
                          onChanged={reload}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        }}
      </Loaded>
      {active && <PlaceHold slug={slug} onPlaced={reload} />}
    </section>
  );
}

function HoldItem({
  slug,
  hold,
  active,
  onChanged,
}: {
  slug: string;
  hold: LegalHoldRecord;
  active: boolean;
  onChanged: () => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [open, setOpen] = useState(false);
  const count = hold.mailboxes.length;
  return (
    <div className="ret-hold">
      <div className="adm-panel-head">
        <h4>
          {hold.matter}{' '}
          <Badge tone={hold.released_at ? 'neutral' : 'warn'}>
            {hold.scope === 'query' ? 'messages' : 'mailboxes'}
          </Badge>
        </h4>
        {active && hold.released_at === null && (
          <ActionButton
            tone="danger"
            run={async () => {
              if (
                window.confirm(
                  `Release the hold for "${hold.matter}"? Retention can then delete the mail it kept, and its mailboxes can be purged.`,
                )
              ) {
                await api(`${base(slug)}/legal-holds/${encodeURIComponent(hold.id)}/release`, {
                  method: 'POST',
                });
                onChanged();
              }
            }}
          >
            Release
          </ActionButton>
        )}
      </div>
      {hold.reason && <p className="adm-small">{hold.reason}</p>}
      <p className="adm-small">
        {hold.scope === 'query' && hold.query ? (
          <>Messages in any mailbox {queryInWords(hold.query)}</>
        ) : (
          <>
            {count} {count === 1 ? 'mailbox' : 'mailboxes'}{' '}
            <button
              type="button"
              className="adm-copy"
              aria-expanded={open}
              onClick={() => setOpen(!open)}
            >
              {open ? 'Hide' : 'Show'}
            </button>
          </>
        )}
      </p>
      {open && hold.scope !== 'query' && (
        <ul className="adm-small adm-mono ret-mailboxes">
          {hold.mailboxes.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
      <p className="adm-small adm-muted">
        Placed {when(hold.placed_at)} by {hold.placed_by}
        {hold.released_at && ` · released ${when(hold.released_at)} by ${hold.released_by ?? '—'}`}
      </p>
    </div>
  );
}

// ─── Placing a hold ──────────────────────────────────────────────────────────

const QUERY_FIELDS: ReadonlyArray<[keyof LegalHoldQuery, string, 'text' | 'date']> = [
  ['from', 'From', 'text'],
  ['to', 'To', 'text'],
  ['subject', 'Subject contains', 'text'],
  ['text', 'Text contains', 'text'],
  ['since', 'Received since', 'date'],
  ['before', 'Received before', 'date'],
];

/** One address per line, or separated by commas. */
export function parseMailboxes(text: string): string[] {
  return text
    .split(/[\s,;]+/)
    .map((m) => m.trim())
    .filter((m) => m !== '');
}

const NO_QUERY: Record<keyof LegalHoldQuery, string> = {
  from: '',
  to: '',
  subject: '',
  text: '',
  since: '',
  before: '',
};

function PlaceHold({ slug, onPlaced }: { slug: string; onPlaced: () => void }) {
  const { base } = useContext(AdminWhereContext);
  const [matter, setMatter] = useState('');
  const [reason, setReason] = useState('');
  const [scope, setScope] = useState<'mailboxes' | 'query'>('mailboxes');
  const [mailboxes, setMailboxes] = useState('');
  const [query, setQuery] = useState(NO_QUERY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [placed, setPlaced] = useState(false);

  const body = (): NewLegalHold => {
    const why = reason.trim();
    const common = { matter: matter.trim(), ...(why ? { reason: why } : {}) };
    if (scope === 'mailboxes') {
      return { ...common, mailboxes: parseMailboxes(mailboxes) };
    }
    const q: Record<string, string> = {};
    for (const [key] of QUERY_FIELDS) {
      const value = query[key].trim();
      if (value) {
        q[key] = value;
      }
    }
    return { ...common, query: q as LegalHoldQuery };
  };

  return (
    <form
      className="adm-sub ret-new"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        setPlaced(false);
        api<{ id: string }>(`${base(slug)}/legal-holds`, { method: 'POST', body: body() })
          .then(() => {
            setPlaced(true);
            setMatter('');
            setReason('');
            setMailboxes('');
            setQuery(NO_QUERY);
            onPlaced();
          })
          .catch((e: unknown) => setError(describeError(e)))
          .finally(() => setBusy(false));
      }}
    >
      <h4>Place a hold</h4>
      <div className="adm-form-grid">
        <label>
          Matter
          <input
            value={matter}
            onChange={(e) => setMatter(e.target.value)}
            placeholder="e.g. Smith v Acme"
            required
          />
        </label>
        <label>
          Reason <span className="adm-muted">(optional)</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
      </div>
      <div className="adm-inline ret-scope" role="radiogroup" aria-label="What to hold">
        <label className="adm-check">
          <input
            type="radio"
            name="ret-scope"
            checked={scope === 'mailboxes'}
            onChange={() => setScope('mailboxes')}
          />
          Whole mailboxes
        </label>
        <label className="adm-check">
          <input
            type="radio"
            name="ret-scope"
            checked={scope === 'query'}
            onChange={() => setScope('query')}
          />
          Messages matching…
        </label>
      </div>
      {scope === 'mailboxes' ? (
        <>
          <label className="adm-small adm-muted" htmlFor="ret-mailboxes">
            One address per line, or separated by commas. Every message in these mailboxes is kept.
          </label>
          <textarea
            id="ret-mailboxes"
            rows={4}
            value={mailboxes}
            onChange={(e) => setMailboxes(e.target.value)}
            aria-label="Mailboxes to hold"
            placeholder="jane@acme.example"
            spellCheck={false}
          />
          <MailboxPicker
            slug={slug}
            chosen={parseMailboxes(mailboxes)}
            onPick={(email) =>
              setMailboxes((current) =>
                current.trim() === '' ? email : `${current.replace(/\s+$/, '')}\n${email}`,
              )
            }
          />
        </>
      ) : (
        <>
          <p className="adm-small adm-muted">
            Messages in any mailbox that match every field you fill in are kept, including mail that
            arrives later.
          </p>
          <div className="adm-form-grid">
            {QUERY_FIELDS.map(([key, label, type]) => (
              <label key={key}>
                {label}
                <input
                  type={type}
                  value={query[key]}
                  onChange={(e) => setQuery({ ...query, [key]: e.target.value })}
                />
              </label>
            ))}
          </div>
        </>
      )}
      <div className="adm-inline">
        <button type="submit" className="btn" disabled={busy || matter.trim() === ''}>
          {busy ? 'Placing…' : 'Place hold'}
        </button>
        {error && (
          <span className="adm-error" role="alert">
            {error}
          </span>
        )}
        {placed && (
          <span className="adm-ok" role="status">
            Hold placed.
          </span>
        )}
      </div>
    </form>
  );
}

/** Adds an address from the organisation's mailboxes; absent if they cannot be listed. */
function MailboxPicker({
  slug,
  chosen,
  onPick,
}: {
  slug: string;
  chosen: readonly string[];
  onPick: (email: string) => void;
}) {
  const { base } = useContext(AdminWhereContext);
  const [load] = useLoad(
    (signal) => api<MailboxList>(`${base(slug)}/mailboxes`, { signal }),
    `hold-mailboxes:${slug}`,
  );
  if (load.state !== 'ready' || !Array.isArray(load.data.mailboxes)) {
    return null;
  }
  const taken = new Set(chosen.map((m) => m.toLowerCase()));
  const left = load.data.mailboxes.filter((m) => !taken.has(m.email.toLowerCase()));
  if (left.length === 0) {
    return null;
  }
  return (
    <label className="adm-small adm-muted ret-pick">
      Add a mailbox{' '}
      <select
        value=""
        aria-label="Add a mailbox"
        onChange={(e) => {
          if (e.target.value) {
            onPick(e.target.value);
          }
        }}
      >
        <option value="">Choose…</option>
        {left.map((m) => (
          <option key={m.email} value={m.email}>
            {m.email}
            {m.state === 'deleted' ? ' (deleted)' : m.state === 'disabled' ? ' (disabled)' : ''}
          </option>
        ))}
      </select>
    </label>
  );
}
